// File: frontend/src/components/seo-analytics/AhrefsCitationsTab.jsx
// Version: 1.1
// Date: 2026-04-27
// Description: AI Citations tab — recharts donut chart showing citation
//              counts across 5 AI surfaces (Google AI Overview, ChatGPT,
//              Perplexity, Gemini, Copilot). Renders an empty-state
//              explanation when all surfaces are NULL (current default
//              state until Brand Radar reports are pre-configured in
//              Ahrefs).
// History:
// - [v1.1]: Light editorial restyle — empty state in white card with
//           subtle border, donut + breakdown wrapped in white cards.
// - [v1.0]: Initial dark theme implementation.

import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import seoAnalyticsService from '../../services/seoAnalyticsService';

const SURFACES = [
  { key: 'aio', label: 'Google AI Overview', color: '#f97316' },
  { key: 'chatgpt', label: 'ChatGPT', color: '#06b6d4' },
  { key: 'perplexity', label: 'Perplexity', color: '#a855f7' },
  { key: 'gemini', label: 'Gemini', color: '#3b82f6' },
  { key: 'copilot', label: 'Copilot', color: '#6b7280' },
];

const formatNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

const AhrefsCitationsTab = ({ domainId }) => {
  const [citations, setCitations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!domainId) return;
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await seoAnalyticsService.getAhrefsCitations(domainId);
        if (!cancelled) setCitations(data || null);
      } catch (err) {
        console.error('[AhrefsCitationsTab] fetch failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load AI citations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [domainId]);

  if (loading) {
    return (
      <div className="ahrefs-citations-tab">
        <div className="chart-loading">Loading AI citations…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ahrefs-citations-tab">
        <div className="inline-error">{error}</div>
      </div>
    );
  }

  const allNull =
    !citations || SURFACES.every((s) => citations[s.key] == null);

  if (allNull) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '2rem',
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
            maxWidth: '640px',
            margin: '0 auto',
          }}
        >
          <h3
            style={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: '#0f172a',
              margin: '0 0 0.75rem 0',
            }}
          >
            AI Citations not yet available
          </h3>
          <p
            style={{
              color: '#475569',
              fontSize: '0.9rem',
              lineHeight: 1.55,
              margin: '0 0 0.75rem 0',
            }}
          >
            AI Citations require pre-configured Brand Radar reports in Ahrefs. Once set
            up, this view shows citation counts across the 5 major AI surfaces:
          </p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 1rem 0',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.5rem',
            }}
          >
            {SURFACES.map((s) => (
              <li
                key={s.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                }}
              >
                <span
                  className="surface-dot"
                  style={{ background: s.color }}
                  aria-hidden="true"
                />
                <span>{s.label}</span>
              </li>
            ))}
          </ul>
          <a
            href="https://ahrefs.com/brand-radar"
            target="_blank"
            rel="noreferrer"
            style={{
              color: '#2563eb',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Learn more about Brand Radar →
          </a>
        </div>
      </div>
    );
  }

  // Build donut data — preserve 0-counts as visible-but-grayed slices.
  const donutData = SURFACES.map((s) => ({
    name: s.label,
    key: s.key,
    value: citations[s.key] ?? 0,
    color: citations[s.key] == null ? '#d1d5db' : s.color,
  })).filter((d) => d.value > 0 || citations[d.key] === 0);

  const total = SURFACES.reduce((sum, s) => sum + (citations[s.key] || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
          AI Surface Citations
        </h3>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '1.125rem',
        }}
        className="citations-grid"
      >
        <div
          className="citations-donut-card"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1rem',
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
            position: 'relative',
          }}
        >
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={donutData}
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="95%"
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                stroke="#ffffff"
                strokeWidth={2}
              >
                {donutData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(val, name) => [formatNum(val), name]}
                contentStyle={{ fontSize: 12 }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value, entry) => {
                  const v = entry?.payload?.value ?? 0;
                  return `${value} (${formatNum(v)})`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="donut-center-label" aria-live="polite">
            <span className="donut-center-value">{formatNum(total)}</span>
            <span className="donut-center-label-text">total citations</span>
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1.25rem',
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
          }}
        >
          <h4
            style={{
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#64748b',
              margin: '0 0 0.75rem 0',
            }}
          >
            Per-surface breakdown
          </h4>
          <ul className="citations-breakdown-list">
            {SURFACES.map((s) => {
              const count = citations[s.key];
              const share = total > 0 && count != null ? (count / total) * 100 : 0;
              return (
                <li key={s.key} className="citations-breakdown-row">
                  <span
                    className="surface-dot"
                    style={{ background: count == null ? '#d1d5db' : s.color }}
                    aria-hidden="true"
                  />
                  <span className="surface-name">{s.label}</span>
                  <span className="surface-count">
                    {count == null ? '—' : formatNum(count)}
                  </span>
                  <span className="surface-share">
                    {count == null ? '—' : `${share.toFixed(1)}%`}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AhrefsCitationsTab;
